import React, { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { getApiBaseUrl, getAuthConfig } from "../../services/configService";
import { LineChart, Line, ResponsiveContainer } from "recharts";

import "./HierarchyReport.css";

const HierarchyReport = () => {
  const { instance, accounts } = useMsal();
  const authConfig = getAuthConfig();

  const [data, setData] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [timeframe, setTimeframe] = useState("cw");

  // ======================
  // FETCH
  // ======================
  const getAccessToken = async () => {
    if (!accounts.length) return null;

    try {
      const res = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    } catch {
      const res = await instance.acquireTokenPopup({
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    }
  };

  const fetchHierarchy = async () => {
    setLoading(true);

    const token = await getAccessToken();
    if (!token) return;

    const res = await fetch(`${getApiBaseUrl()}/hierarchy-report`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const json = await res.json();
    setData(json);
    setLoading(false);
  };

  useEffect(() => {
    fetchHierarchy();
  }, []);

  // ======================
  // TREE STRUCTURE
  // ======================
  const byId = useMemo(() => {
    const map = new Map();

    data.forEach((d) => {
      map.set(d.id, { ...d, children: [] });
    });

    data.forEach((d) => {
      if (d.supervisor_id) {
        map.get(d.supervisor_id)?.children.push(d.id);
      }
    });

    return map;
  }, [data]);

  const root = useMemo(() => {
    const ids = new Set(data.map((d) => d.id));
    return data.find((d) => !d.supervisor_id || !ids.has(d.supervisor_id));
  }, [data]);

  // ======================
  // ROLLUP (✅ FIXED)
  // ======================
  const rollupCache = useMemo(() => new Map(), [timeframe]);

  const rollup = (id) => {
    if (rollupCache.has(id)) return rollupCache.get(id);

    const node = byId.get(id);
    if (!node) return null;

    // ✅ FFC
    if (node.is_ffc) {
      const isPeriod = timeframe === "cp" || timeframe === "lp";
    
      const trendSource = isPeriod
        ? node.trend?.periods || []
        : node.trend?.weekly || [];
    
      const latest = trendSource.find(w => w.key === timeframe) || {};
    
      const recovered = latest.recovered || 0;
      const visits = latest.visits || 0;
    
      const revenueGoal = isPeriod ? 40000 : 10000;
      const patientGoal = isPeriod ? 400 : 100; 
    
      const avg =
        ((revenueGoal ? recovered / revenueGoal : 0) +
         (patientGoal ? visits / patientGoal : 0)) / 2;
    
      const result = {
        recovered,
        visits,
        avg,
        trend: trendSource,
        revenueGoal,
        patientGoal
      };
    
      rollupCache.set(id, result);
      return result;
    }    

    // ✅ AGGREGATE
    let recovered = 0;
    let visits = 0;
    let revenueGoal = 0;
    let patientGoal = 0;

    const trendMap = {};

    node.children.forEach((childId) => {
      const c = rollup(childId);

      recovered += c.recovered;
      visits += c.visits;
      revenueGoal += c.revenueGoal;
      patientGoal += c.patientGoal;

      c.trend.forEach((w) => {
        if (!trendMap[w.key]) {
          trendMap[w.key] = { ...w };
        } else {
          trendMap[w.key].recovered += w.recovered || 0;
          trendMap[w.key].visits += w.visits || 0;
        }
      });
    });

    const trend = Object.values(trendMap);

    const avg =
      ((revenueGoal ? recovered / revenueGoal : 0) +
        (patientGoal ? visits / patientGoal : 0)) / 2;

    const result = {
      recovered,
      visits,
      avg,
      trend,
      revenueGoal,
      patientGoal,
    };

    rollupCache.set(id, result);
    return result;
  };

  // ======================
  // EXPAND / COLLAPSE
  // ======================
  const toggleRow = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ======================
  // TABLE ROW RENDER
  // ======================
  const renderRow = (id, depth = 0) => {
    const node = byId.get(id);
    if (!node) return null;

    const t = rollup(id);
    const isOpen = expanded.has(id);

    const revenueGoal = t.revenueGoal;
    const patientGoal = t.patientGoal;

    return (
      <React.Fragment key={id}>
        <div
          className={`tree-row 
            ${node.is_ffc ? "ffc" : ""} 
            role-${node.role?.toLowerCase()?.replace(/\s+/g, "-")}
          `}
          style={{ paddingLeft: `${depth * 20}px` }}
          onClick={() => toggleRow(id)}
        >
          <div className="cell name">
            {node.children.length > 0 && (
              <span className="expand">{isOpen ? "▼" : "▶"}</span>
            )}
            {node.name}
            <span className="role-full">({node.role})</span>
          </div>

          <div className="cell">${t.recovered.toLocaleString()}</div>
          <div className="cell">${revenueGoal.toLocaleString()}</div>
          <div className="cell">
            {Math.round((t.recovered / revenueGoal) * 100)}%
          </div>

          <div className="cell">{t.visits.toLocaleString()}</div>
          <div className="cell">{patientGoal.toLocaleString()}</div>
          <div className="cell">
            {Math.round((t.visits / patientGoal) * 100)}%
          </div>

          <div
            className="cell score"
            style={{ color: getPerformanceColor(t.avg) }}
          >
            {Math.round(t.avg * 100)}%
          </div>

          <div className="cell spark">
            <ResponsiveContainer width="100%" height={30}>
              <LineChart data={t.trend || []}>
                <Line
                  dataKey="recovered"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {isOpen && (
          <div className="tree-children">
            {node.children.map((childId) =>
              renderRow(childId, depth + 1)
            )}
          </div>
        )}
      </React.Fragment>
    );
  };

  // ======================
  // LEVEL INIT
  // ======================
  useEffect(() => {
    if (!root) return;

    const rootNode = byId.get(root.id);
    const children = rootNode?.children || [];

    setLevels([
      { nodes: [root.id], selectedId: root.id },
      { nodes: children, selectedId: children[0] },
    ]);
  }, [root, byId]);

  // ======================
  // SELECT
  // ======================
  const handleSelect = (levelIndex, nodeId) => {
    setLevels((prev) => {
      const node = byId.get(nodeId);
      const nextChildren = node?.children || [];

      const newLevels = prev.slice(0, levelIndex + 1);

      newLevels[levelIndex] = {
        ...newLevels[levelIndex],
        selectedId: nodeId,
      };

      if (nextChildren.length) {
        newLevels.push({
          nodes: nextChildren,
          selectedId: nextChildren[0],
        });
      }

      return newLevels;
    });
  };

  const getPerformanceColor = (pct) => {
    const clamped = Math.min(Math.max(pct, 0), 1);
    const hue = clamped * 120;
    return `hsl(${hue}, 70%, 45%)`;
  };

  if (loading) return <div>Loading...</div>;

  // ======================
  // UI
  // ======================
  return (
    <>
      {/*<div className="hierarchy-horizontal">
        {levels.map((level, levelIndex) => (
          <div className="column" key={levelIndex}>
            {level.nodes.map((id) => {
              const node = byId.get(id);
              const t = rollup(id);
              const selected = level.selectedId === id;
              const isFlat = levelIndex < levels.length - 1;

              const displayName =
                levelIndex === 0 ? "Company Name" : node.name;

              const badge =
                levelIndex === 0 ? "CMP" : node.role;

              return (
                <div
                  key={id}
                  className={`hier-card 
                    ${selected ? "selected" : ""} 
                    ${isFlat ? "flat" : ""} 
                    ${node.is_ffc ? "ffc" : ""}
                  `}
                  onClick={() =>
                    handleSelect(levelIndex, id)
                  }
                >

                  {displayName}
                </div>
              );
            })}
          </div>
        ))}
      </div> */}


      <div className="report-header">

        {/* LEFT SIDE */}
        <div className="header-left-section">
          <div className="header-title">
            Organizational Performance Explorer
          </div>

          <div className="header-sub">
            Navigate your hierarchy, expand teams, and analyze performance across revenue and patient metrics.
          </div>

          <div className="header-instructions">
            Click rows to expand • Use filters to refine view • Colors indicate performance vs goal
          </div>
        </div>

        {/* RIGHT SIDE (FILTERS) */}
        <div className="header-filters">
          <div className="filter">
            <label>Timeframe</label>
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
            >
              <option value="cw">Current Week</option>
              <option value="lw">Last Week</option>
              <option value="cp">Current Period</option>
              <option value="lp">Last Period</option>
            </select>
          </div>
        </div>

      </div>


      {/* TABLE */}
      <div className="hierarchy-table">
        <div className="tree-header">
          <div className="cell name">Name</div>
          <div className="cell">Revenue</div>
          <div className="cell">Goal</div>
          <div className="cell">Revenue %</div>
          <div className="cell">Visits</div>
          <div className="cell">Goal</div>
          <div className="cell">Visit %</div>
          <div className="cell">Score</div>
          <div className="cell">Trend</div>
        </div>

        {root && renderRow(root.id)}
      </div>
    </>
  );
};

export default HierarchyReport;